import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem { id: number; message: string; tone: ToastTone }
interface ConfirmOptions {
  title: string;
  body: string;
  confirmText?: string;
  danger?: boolean;
  hideCancel?: boolean;
}
interface UiContextValue {
  toast: (message: string, tone?: ToastTone) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const UiContext = createContext<UiContextValue | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [dialog, setDialog] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const toast = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(items => [...items, { id, message, tone }]);
    window.setTimeout(() => setToasts(items => items.filter(item => item.id !== id)), 3200);
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    setDialog(options);
    return new Promise<boolean>(resolve => {
      resolver.current = resolve;
    });
  }, []);

  const finish = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setDialog(null);
  };

  return (
    <UiContext.Provider value={{ toast, confirm }}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {toasts.map(item => (
          <div className={'toast toast-' + item.tone} key={item.id}>
            {item.tone === 'success' ? <CheckCircle2 size={18} /> : item.tone === 'error' ? <AlertTriangle size={18} /> : <Info size={18} />}
            <span>{item.message}</span>
          </div>
        ))}
      </div>
      {dialog && (
        <div className="modal-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && finish(false)}>
          <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
            <button className="icon-button modal-close" onClick={() => finish(false)} aria-label="关闭"><X size={18} /></button>
            <div className={'confirm-mark ' + (dialog.danger ? 'danger' : '')}><AlertTriangle size={22} /></div>
            <h3 id="confirm-title">{dialog.title}</h3>
            <p className="muted">{dialog.body}</p>
            <div className="modal-actions">
              {!dialog.hideCancel && <button className="button ghost" onClick={() => finish(false)}>取消</button>}
              <button className={'button ' + (dialog.danger ? 'danger' : 'primary')} onClick={() => finish(true)}>
                {dialog.confirmText || '确认'}
              </button>
            </div>
          </div>
        </div>
      )}
    </UiContext.Provider>
  );
}

export function useUi() {
  const value = useContext(UiContext);
  if (!value) throw new Error('useUi must be used inside UiProvider');
  return value;
}
